import React, { useState, useEffect, useCallback } from 'react';
import {
    MessageSquare,
    Plus,
    Trash2,
    Search,
    Loader2,
    Clock,
    ChevronRight,
    AlertCircle,
    Archive
} from 'lucide-react';
import {
    claudeQueryService,
    ConversationListDTO
} from '../../../../../services/claudeQueryService';
import { useTranslation } from '../../../../../contexts/I18nProvider';

interface ConversationListProps {
    activeConversationId: number | null;
    onSelectConversation: (id: number) => void;
    onNewConversation: () => void;
    onClose: () => void;
}

export const ConversationList: React.FC<ConversationListProps> = ({
    activeConversationId,
    onSelectConversation,
    onNewConversation,
    onClose
}) => {
    const { t } = useTranslation();
    const [conversations, setConversations] = useState<ConversationListDTO[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [searchQuery, setSearchQuery] = useState('');

    const loadConversations = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            const data = await claudeQueryService.getConversations();
            setConversations(data);
        } catch (err: any) {
            setError(err.message || t('manager.modules.claudeQuery.conversations.loadError', {}, 'Kunne ikke laste samtaler'));
        } finally {
            setLoading(false);
        }
    }, [t]);

    useEffect(() => {
        loadConversations();
    }, [loadConversations]);

    const handleDelete = async (e: React.MouseEvent, id: number) => {
        e.stopPropagation();
        if (!window.confirm(t('manager.modules.claudeQuery.conversations.deleteConfirm', {}, 'Er du sikker på at du vil slette denne samtalen?'))) return;

        try {
            await claudeQueryService.deleteConversation(id);
            setConversations(prev => prev.filter(c => c.conversationId !== id));
        } catch (err: any) {
            setError(t('manager.modules.claudeQuery.conversations.deleteError', { '0': err.message }, `Feil ved sletting: ${err.message}`));
        }
    };

    const formatDate = (dateStr: string) => {
        const d = new Date(dateStr);
        const now = new Date();
        const diffMs = now.getTime() - d.getTime();
        const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

        if (diffDays === 0) {
            return d.toLocaleTimeString('nb-NO', { hour: '2-digit', minute: '2-digit' });
        } else if (diffDays === 1) {
            return t('manager.modules.claudeQuery.conversations.yesterday', {}, 'I går');
        } else if (diffDays < 7) {
            return t('manager.modules.claudeQuery.conversations.daysAgo', { '0': diffDays }, `${diffDays} dager siden`);
        } else {
            return d.toLocaleDateString('nb-NO', { day: '2-digit', month: '2-digit', year: '2-digit' });
        }
    };

    // Group conversations by time period
    const groupConversations = (items: ConversationListDTO[]) => {
        const groups: { label: string; items: ConversationListDTO[] }[] = [];
        const today: ConversationListDTO[] = [];
        const yesterday: ConversationListDTO[] = [];
        const thisWeek: ConversationListDTO[] = [];
        const thisMonth: ConversationListDTO[] = [];
        const older: ConversationListDTO[] = [];

        const now = new Date();
        const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        const yesterdayStart = new Date(todayStart);
        yesterdayStart.setDate(yesterdayStart.getDate() - 1);
        const weekStart = new Date(todayStart);
        weekStart.setDate(weekStart.getDate() - 7);
        const monthStart = new Date(todayStart);
        monthStart.setDate(monthStart.getDate() - 30);

        items.forEach(c => {
            const d = new Date(c.lastMessageAt || c.createdAt);
            if (d >= todayStart) today.push(c);
            else if (d >= yesterdayStart) yesterday.push(c);
            else if (d >= weekStart) thisWeek.push(c);
            else if (d >= monthStart) thisMonth.push(c);
            else older.push(c);
        });

        if (today.length > 0) groups.push({ label: t('manager.modules.claudeQuery.conversations.groupToday', {}, 'I dag'), items: today });
        if (yesterday.length > 0) groups.push({ label: t('manager.modules.claudeQuery.conversations.yesterday', {}, 'I går'), items: yesterday });
        if (thisWeek.length > 0) groups.push({ label: t('manager.modules.claudeQuery.conversations.groupThisWeek', {}, 'Denne uken'), items: thisWeek });
        if (thisMonth.length > 0) groups.push({ label: t('manager.modules.claudeQuery.conversations.groupThisMonth', {}, 'Denne måneden'), items: thisMonth });
        if (older.length > 0) groups.push({ label: t('manager.modules.claudeQuery.conversations.groupOlder', {}, 'Eldre'), items: older });

        return groups;
    };

    // Filter by search
    const filteredConversations = conversations.filter(c => {
        if (!searchQuery) return true;
        const q = searchQuery.toLowerCase();
        return c.title?.toLowerCase().includes(q);
    });

    const groups = groupConversations(filteredConversations);

    return (
        <div className="flex flex-col h-full bg-white border-r border-gray-200">
            {/* Header */}
            <div className="px-4 py-3 border-b border-gray-100">
                <div className="flex items-center justify-between mb-3">
                    <h2 className="text-sm font-semibold text-gray-700">{t('manager.modules.claudeQuery.conversations.title', {}, 'Samtalehistorikk')}</h2>
                    <button
                        onClick={onNewConversation}
                        className="flex items-center gap-1 px-2.5 py-1 bg-blue-500 text-white rounded-md text-xs
                                 hover:bg-blue-600 font-medium transition-colors"
                        title={t('manager.modules.claudeQuery.chat.newConversation', {}, 'Ny samtale')}
                    >
                        <Plus size={12} />
                        {t('manager.modules.claudeQuery.conversations.new', {}, 'Ny')}
                    </button>
                </div>

                {/* Search */}
                <div className="relative">
                    <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input
                        type="text"
                        placeholder={t('manager.modules.claudeQuery.conversations.searchPlaceholder', {}, 'Søk i samtaler...')}
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="w-full pl-8 pr-3 py-1.5 border border-gray-200 rounded-md text-xs
                                 focus:outline-none focus:border-blue-400 focus:ring-1 focus:ring-blue-200"
                    />
                </div>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-auto">
                {loading ? (
                    <div className="flex items-center justify-center h-32">
                        <Loader2 className="animate-spin text-blue-500" size={20} />
                        <span className="ml-2 text-xs text-gray-500">{t('common.status.loading', {}, 'Laster...')}</span>
                    </div>
                ) : error ? (
                    <div className="p-3">
                        <div className="flex items-center gap-2 p-2 bg-red-50 border border-red-200 rounded text-xs text-red-700">
                            <AlertCircle size={14} />
                            {error}
                        </div>
                    </div>
                ) : filteredConversations.length === 0 ? (
                    <div className="flex flex-col items-center justify-center h-32 text-gray-400 px-4">
                        <MessageSquare size={24} className="mb-2 text-gray-300" />
                        <p className="text-xs text-center">
                            {conversations.length === 0
                                ? t('manager.modules.claudeQuery.conversations.emptyNone', {}, 'Ingen lagrede samtaler ennå')
                                : t('manager.modules.claudeQuery.conversations.emptySearch', {}, 'Ingen samtaler matcher søket')}
                        </p>
                    </div>
                ) : (
                    <div className="py-1">
                        {groups.map(group => (
                            <div key={group.label}>
                                <div className="px-4 py-1.5 text-[10px] font-semibold text-gray-400 uppercase tracking-wider">
                                    {group.label}
                                </div>
                                {group.items.map(conversation => (
                                    // Rad med to søsken-knapper (velg / slett) — knapp i knapp er ugyldig HTML,
                                    // og en slette-knapp som bare vises ved hover kan ikke nås på nettbrett
                                    <div
                                        key={conversation.conversationId}
                                        className={`flex items-center hover:bg-gray-50 transition-colors ${
                                            activeConversationId === conversation.conversationId
                                                ? 'bg-blue-50 border-r-2 border-blue-500'
                                                : ''
                                        }`}
                                    >
                                    <button
                                        onClick={() => onSelectConversation(conversation.conversationId)}
                                        className="flex-1 min-w-0 text-left pl-4 pr-1 py-2.5 min-h-[44px] flex items-center gap-2"
                                    >
                                        <MessageSquare
                                            size={14}
                                            className={activeConversationId === conversation.conversationId
                                                ? 'text-blue-500 flex-shrink-0'
                                                : 'text-gray-400 flex-shrink-0'}
                                        />
                                        <div className="flex-1 min-w-0">
                                            <p className={`text-xs font-medium truncate ${
                                                activeConversationId === conversation.conversationId
                                                    ? 'text-blue-700'
                                                    : 'text-gray-700'
                                            }`}>
                                                {conversation.title || t('manager.modules.claudeQuery.conversations.untitled', {}, 'Uten tittel')}
                                            </p>
                                            <div className="flex items-center gap-1.5 mt-0.5">
                                                <Clock size={10} className="text-gray-300" />
                                                <span className="text-[10px] text-gray-400">
                                                    {formatDate(conversation.lastMessageAt || conversation.createdAt)}
                                                </span>
                                                <span className="text-[10px] text-gray-300">
                                                    {t('manager.modules.claudeQuery.conversations.messageCount', { '0': conversation.messageCount }, `${conversation.messageCount} meldinger`)}
                                                </span>
                                            </div>
                                        </div>
                                    </button>
                                    <button
                                        onClick={(e) => handleDelete(e, conversation.conversationId)}
                                        className="flex items-center justify-center min-w-[44px] min-h-[44px] mr-1 text-gray-300 hover:text-red-500 rounded transition-colors flex-shrink-0"
                                        aria-label={t('manager.modules.claudeQuery.conversations.deleteTitle', {}, 'Slett samtale')}
                                    >
                                        <Trash2 size={14} />
                                    </button>
                                    </div>
                                ))}
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};
